import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, Command, Keyboard, Mail, Plug, Search, Users } from "lucide-react";
import { Button, Field, Input, Kbd, Pill, Spinner, toast } from "@/ui";
import { useSession } from "@/app/session";
import { hostMode, getHost, type HostIntegrationStatus } from "@/engine/transport";
import { invokeEngine } from "@/engine/hooks";
import { useEngineStatus } from "@/app/RequireSession";
import { NetworkOnboardingStep } from "@/features/network";
import { ImportPanel } from "@/features/settings/ImportPanel";
import { ApiError } from "@/api/client";
import { SERVER_URL_EDITABLE } from "@/app/server-url";
import s from "./onboarding.module.css";

type Step = "welcome" | "signin" | "code" | "workspace" | "name" | "sources" | "network" | "done";
const ORDER: Step[] = ["welcome", "signin", "code", "workspace", "name", "sources", "network", "done"];

/** Your name goes on your own card: that is what people on Hub see (the server reads it from there). */
async function saveOwnName(firstName: string, lastName: string, email: string | null): Promise<void> {
  const input = { firstName: firstName.trim() || null, lastName: lastName.trim() || null, displayName: [firstName.trim(), lastName.trim()].filter(Boolean).join(" ") || null };
  const self = await invokeEngine("getSelfContact", undefined);
  if (self) {
    await invokeEngine("updateContact", { contactId: self.contact.id, input });
    return;
  }
  const created = await invokeEngine("createContact", { ...input, emails: email ? [{ value: email }] : [], source: "hub" });
  await invokeEngine("setSelfContact", { contactId: created.contactId });
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  // Signing in to open a workspace invitation goes straight back to it; setup can wait.
  const from = (location.state as { from?: string } | null)?.from ?? "";
  const backToInvite = from.startsWith("/invite/") ? from : null;
  const session = useSession();
  const engine = useEngineStatus();
  // Served by the Hub server there is nothing to keep "on this device", so the web starts at sign-in.
  const web = hostMode() === "web";
  // Already signed in (a reload part way through setup): carry on from the workspace, never ask to sign in again.
  const [step, setStep] = useState<Step>(params.get("step") === "signin" ? "signin" : session.user && session.workspaceId && (session.token || !web) ? "workspace" : web ? "signin" : "welcome");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [wsName, setWsName] = useState("Personal");
  const [firstName, setFirstName] = useState(session.user?.firstName ?? "");
  const [lastName, setLastName] = useState(session.user?.lastName ?? "");
  const [savingName, setSavingName] = useState(false);
  const [tour, setTour] = useState(false);

  const signedIn = !!session.user && !!session.workspaceId;
  /** An account that existed before this sign-in has its people already; only a new one walks through setup. */
  const returningAccount = () => {
    const u = useSession.getState().user;
    return !!u && Date.now() - Date.parse(u.created) > 10 * 60 * 1000;
  };
  const next = () => setStep((st) => ORDER[Math.min(ORDER.length - 1, ORDER.indexOf(st) + 1)]);

  const finish = () => {
    session.setOnboarded(true);
    setTour(true);
  };

  if (tour) return <Tour onDone={() => navigate("/", { replace: true })} />;

  return (
    <div className={s.wrap}>
      <div className={s.hero}>
        <div className={s.heroInner}>
          <svg className={s.logo} viewBox="0 0 100 100" aria-hidden="true"><rect width="100" height="100" rx="24" fill="#0f1012" /><rect x="16" y="16" width="20" height="68" rx="3" fill="#f2f2f3" /><rect x="64" y="16" width="20" height="68" rx="3" fill="#f2f2f3" /><rect x="36" y="42" width="28" height="16" fill="#60a5fa" /></svg>
          <h1>Ask your people, not the internet.</h1>
          <p>The Hub keeps you close to the people who matter and finds the introductions only you can make, through the people you actually know.</p>
          <ul className={s.heroList}>
            <li><Check size={14} /> Your people on your iPhone, your Mac and here, in one workspace.</li>
            <li><Check size={14} /> Never messages anyone or acts on your behalf.</li>
            <li><Check size={14} /> From Messages and Mail it keeps only who and when, never the content.</li>
          </ul>
        </div>
      </div>
      <div className={s.panel}>
        <div className={s.steps} aria-label="Progress">
          {ORDER.filter((st) => st !== "code").map((st) => <span key={st} className={s.stepDot} data-active={ORDER.indexOf(st) <= ORDER.indexOf(step === "code" ? "signin" : step) ? "true" : undefined} />)}
        </div>

        {step === "welcome" && (
          <div className={s.card}>
            <h2>Welcome</h2>
            <p className="u-muted">Sign in to sync across devices and use the network, or keep everything on this device.</p>
            <Button variant="primary" size="lg" full icon={<Mail size={16} />} onClick={() => setStep("signin")}>Sign in with email</Button>
            <Button variant="outline" size="lg" full onClick={() => { session.continueOffline(); setStep("workspace"); }}>Continue offline</Button>
            <div className="u-small u-muted">Offline mode is local only. You can sign in later from Settings.</div>
          </div>
        )}

        {step === "signin" && (
          <form className={s.card} onSubmit={async (e) => { e.preventDefault(); try { await session.requestCode(email); setStep("code"); } catch (err) { toast(err instanceof ApiError && err.isNetwork ? `Cannot reach ${session.serverUrl}. Is the server running?` : session.error ?? "Could not send a code", "danger"); } }}>
            <h2>Sign in</h2>
            <p className="u-muted">We email you a six digit code. No passwords.</p>
            <Field label="Email"><Input autoFocus type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" /></Field>
            <Button type="submit" variant="primary" size="lg" full loading={session.busy}>Send code</Button>
            {!web && <Button variant="ghost" onClick={() => setStep("welcome")}>Back</Button>}
            {SERVER_URL_EDITABLE && <div className="u-small u-muted">Server: <span className="u-mono">{session.serverUrl}</span>. Change it in Settings, Sync.</div>}
          </form>
        )}

        {step === "code" && (
          <form className={s.card} onSubmit={async (e) => { e.preventDefault(); try { await session.verifyCode(code); if (backToInvite) { session.setOnboarded(true); navigate(backToInvite, { replace: true }); } else if (returningAccount()) { session.setOnboarded(true); navigate("/", { replace: true }); } else setStep("workspace"); } catch { toast(session.error ?? "That code did not work", "danger"); } }}>
            <h2>Check your email</h2>
            <p className="u-muted">Enter the code sent to {session.pendingEmail}.</p>
            {session.devCode && <div className={s.devCode}>Development server: your code is <strong className="u-mono">{session.devCode}</strong></div>}
            <Field label="Code"><Input autoFocus inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className="u-mono" style={{ fontSize: 20, letterSpacing: 4, textAlign: "center" }} /></Field>
            <Button type="submit" variant="primary" size="lg" full loading={session.busy} disabled={code.trim().length < 4}>Continue</Button>
            <Button variant="ghost" onClick={() => setStep("signin")}>Use a different email</Button>
          </form>
        )}

        {step === "workspace" && (
          <div className={s.card}>
            <h2>Workspace</h2>
            <p className="u-muted">A workspace holds your people, notes and groups. Teams share one.</p>
            {session.workspaces.length > 0 && (
              <div className="u-col" style={{ gap: 6 }}>
                {session.workspaces.map((w) => (
                  <button key={w.id} type="button" className={s.choice} data-active={w.id === session.workspaceId ? "true" : undefined} onClick={() => session.setWorkspace(w.id)}>
                    <Users size={16} /> <span className="u-grow">{w.name}</span> <Pill>{w.role}</Pill>
                  </button>
                ))}
              </div>
            )}
            <div className="u-row">
              <Input value={wsName} onChange={(e) => setWsName(e.target.value)} placeholder="New workspace name" />
              <Button onClick={() => session.createWorkspace(wsName.trim() || "Personal").then(() => toast("Workspace created", "success"))} disabled={!wsName.trim()}>Create</Button>
            </div>
            <Button variant="primary" size="lg" full disabled={!signedIn} onClick={next} icon={<ArrowRight size={16} />}>Continue</Button>
          </div>
        )}

        {step === "name" && (
          <form
            className={s.card}
            onSubmit={async (e) => {
              e.preventDefault();
              setSavingName(true);
              try {
                await saveOwnName(firstName, lastName, session.user?.email ?? null);
                next();
              } catch {
                toast("Could not save your name. You can set it later on your own card.", "danger");
                next();
              } finally {
                setSavingName(false);
              }
            }}
          >
            <h2>Your name</h2>
            <p className="u-muted">This is how people on Hub see you when you connect, share or message.</p>
            <div className="u-row">
              <Field label="First name"><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoFocus autoComplete="given-name" /></Field>
              <Field label="Last name"><Input value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" /></Field>
            </div>
            <Button type="submit" variant="primary" size="lg" full disabled={!firstName.trim() || savingName || engine.status !== "ready"} loading={savingName} icon={<ArrowRight size={16} />}>Continue</Button>
          </form>
        )}

        {step === "sources" && (
          <div className={s.card}>
            <h2>Bring in your people</h2>
            {engine.status !== "ready" && <div className="u-row u-muted"><Spinner /> Preparing your workspace</div>}
            {engine.status === "ready" && (hostMode() === "desktop" && getHost()?.integrations ? <DesktopSources /> : <ImportPanel onImported={(r) => { toast(`Imported ${r.created} people. Next: what you need and what you offer.`, "success", { label: "Add them now", onClick: next }); }} />)}
            <div className="u-row" style={{ justifyContent: "space-between" }}>
              <Button variant="ghost" onClick={next}>Skip for now</Button>
              <Button variant="primary" onClick={next} icon={<ArrowRight size={16} />}>Continue</Button>
            </div>
          </div>
        )}

        {step === "network" && (
          <div className={s.card}>
            <h2>What you need, what you offer</h2>
            <p className="u-muted">Hub matches needs and offers across the people you know.</p>
            {engine.status === "ready" ? <NetworkOnboardingStep onDone={next} /> : <Spinner />}
            <Button variant="ghost" onClick={next}>Skip</Button>
          </div>
        )}

        {step === "done" && (
          <div className={s.card}>
            <h2>All set</h2>
            <p className="u-muted">Next: a thirty second tour of the command bar and keyboard.</p>
            <Button variant="primary" size="lg" full onClick={finish} icon={<Keyboard size={16} />}>Take the tour</Button>
            <Button variant="ghost" onClick={() => { session.setOnboarded(true); navigate("/", { replace: true }); }}>Skip to Home</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function DesktopSources() {
  const host = getHost();
  const [list, setList] = useState<HostIntegrationStatus[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const refresh = () => host?.integrations?.list().then(setList).catch(() => setList([]));
  useEffect(() => { refresh(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!list) return <Spinner />;
  return (
    <div className="u-col" style={{ gap: 6 }}>
      {list.filter((i) => i.available).map((i) => (
        <div key={i.id} className={s.choice} style={{ cursor: "default" }}>
          <Plug size={16} />
          <span className="u-grow" style={{ textTransform: "capitalize" }}>{i.id.replace(/_/g, " ")}</span>
          {i.authorized ? <Pill tone="success">Connected</Pill> : <Button size="sm" loading={busy === i.id} onClick={async () => { setBusy(i.id); try { await host!.integrations!.authorize(i.id); await host!.integrations!.sync(i.id); refresh(); } catch (e) { toast(e instanceof Error ? e.message : "Could not connect", "danger"); } finally { setBusy(null); } }}>Connect</Button>}
        </div>
      ))}
    </div>
  );
}

const TOUR = [
  { icon: Command, title: "The command bar", body: "Press Mod+K anywhere. Type what you want: a person, a page, or an action like Add a note. Synonyms work too." },
  { icon: Search, title: "Search that understands you", body: "Press / to search. Try company:acme, lastmet<90d or closest friends. Save any search as a smart group." },
  { icon: Keyboard, title: "Keyboard first", body: "Arrows move, Enter opens. On a person: n note, r reminder, l group, h cadence, s star, # archive. g then h, p or s jumps around." },
  { icon: Users, title: "Needs and offers", body: "Record what people need and what they offer. Hub finds matches inside your network and asks you before anything is shared." },
];

export function Tour({ onDone }: { onDone: () => void }) {
  const [i, setI] = useState(0);
  const step = TOUR[i];
  const Icon = step.icon;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "Enter") {
        if (i + 1 < TOUR.length) setI(i + 1);
        else onDone();
      }
      if (e.key === "ArrowLeft") setI(Math.max(0, i - 1));
      if (e.key === "Escape") onDone();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [i, onDone]);
  return (
    <div className={s.tourOverlay} role="dialog" aria-modal="true" aria-label="Tour">
      <div className={s.tourCard}>
        <div className={s.tourIcon}><Icon size={22} /></div>
        <div className="u-small u-muted">Step {i + 1} of {TOUR.length}</div>
        <h2 style={{ margin: "4px 0" }}>{step.title}</h2>
        <p className="u-muted" style={{ margin: 0 }}>{step.body}</p>
        {i === 0 && <div className="u-row" style={{ gap: 4, marginTop: 8 }}><Kbd>{navigator.platform.includes("Mac") ? "Cmd" : "Ctrl"}</Kbd><Kbd>K</Kbd></div>}
        <div className="u-row" style={{ justifyContent: "space-between", marginTop: 16 }}>
          <Button variant="ghost" onClick={onDone}>Skip</Button>
          <span className="u-row">
            {i > 0 && <Button onClick={() => setI(i - 1)}>Back</Button>}
            <Button variant="primary" onClick={() => (i + 1 < TOUR.length ? setI(i + 1) : onDone())}>{i + 1 < TOUR.length ? "Next" : "Start using Hub"}</Button>
          </span>
        </div>
      </div>
    </div>
  );
}
